import MindrailLogo from "./MindrailLogo";
import {
  ListTodo,
  Snowflake,
  Leaf,
  Cloud,
  Home,
  History,
  BarChart3,
  Settings as SettingsIcon,
  ChevronRight,
  Pin,
  Star,
  MoreHorizontal,
  Flame,
} from "lucide-react";

export default function SidePanelMockup() {
  return (
    <div
      data-testid="side-panel-mockup"
      className="relative w-full max-w-[400px]"
    >
      {/* ambient blue glow */}
      <div
        aria-hidden
        className="absolute -inset-6 rounded-[2.5rem] blur-2xl opacity-50 -z-10"
        style={{
          background:
            "radial-gradient(circle, rgba(37,99,235,0.18) 0%, rgba(124,58,237,0.10) 50%, rgba(248,250,252,0) 80%)",
        }}
      />

      <div className="bg-white border border-[#E2E8F0] rounded-3xl shadow-[0_24px_70px_rgba(15,23,42,0.10)] overflow-hidden flex flex-col h-[640px]">
        {/* Header */}
        <div className="px-5 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MindrailLogo variant="png-mark" size={24} />
            <span className="font-display text-sm font-semibold text-[#0F172A]">
              Mindrail
            </span>
          </div>
          <div className="flex gap-3 text-[#94A3B8]">
            <Pin className="w-4 h-4" strokeWidth={1.5} />
            <Star className="w-4 h-4" strokeWidth={1.5} />
            <MoreHorizontal className="w-4 h-4" strokeWidth={1.5} />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 px-4 py-4 space-y-3 overflow-hidden bg-[#F8FAFC]">
          {/* Current Thread (blue card) */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 flex items-start gap-3">
            <span className="shrink-0 w-10 h-10 rounded-xl bg-[#2563EB] flex items-center justify-center">
              <ListTodo className="w-5 h-5 text-white" strokeWidth={1.75} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-label font-medium text-[#475569]">
                Current Thread
              </p>
              <p className="font-display text-[15px] font-semibold text-[#0F172A] mt-0.5 leading-snug">
                Write landing page headline
              </p>
              <div className="mt-2 flex items-center gap-2 text-[11px] text-[#475569]">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#DBEAFE] text-[#1d4ed8] font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB]" /> In
                  progress
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock /> 12 min focused
                </span>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#94A3B8] mt-2" />
          </div>

          {/* I'm Frozen (amber soft card) */}
          <div className="bg-[#FEF3C7] border border-[#F59E0B]/25 rounded-2xl p-4 flex items-start gap-3">
            <span className="shrink-0 w-10 h-10 rounded-xl bg-white flex items-center justify-center">
              <Snowflake
                className="w-5 h-5 text-[#F59E0B]"
                strokeWidth={1.75}
              />
            </span>
            <div className="flex-1">
              <p className="font-display text-[15px] font-semibold text-[#0F172A]">
                I&apos;m Frozen
              </p>
              <p className="text-xs text-[#475569] mt-0.5">
                Get unstuck with a 30-second reset.
              </p>
            </div>
            <button
              type="button"
              className="shrink-0 self-center text-xs font-medium text-[#0F172A] bg-white border border-[#F59E0B]/30 rounded-full px-3 py-1.5 hover:bg-[#F59E0B] hover:text-white hover:border-[#F59E0B] transition-colors"
            >
              Help me start
            </button>
          </div>

          {/* Clean Mode (green soft card with toggle) */}
          <div className="bg-[#DCFCE7] border border-[#22C55E]/25 rounded-2xl p-4 flex items-center gap-3">
            <span className="shrink-0 w-10 h-10 rounded-xl bg-white flex items-center justify-center">
              <Leaf className="w-5 h-5 text-[#22C55E]" strokeWidth={1.75} />
            </span>
            <div className="flex-1">
              <p className="font-display text-[15px] font-semibold text-[#0F172A]">
                Clean Mode
              </p>
              <p className="text-xs text-[#475569] mt-0.5">
                Hide clutter. Show what matters.
              </p>
            </div>
            <span className="relative inline-block w-10 h-6 rounded-full bg-[#22C55E]">
              <span className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-white shadow" />
            </span>
          </div>

          {/* Parked Thoughts (coral soft card) */}
          <div className="bg-[#FFE4DE] border border-[#F9735B]/20 rounded-2xl p-4 flex items-center gap-3">
            <span className="shrink-0 w-10 h-10 rounded-xl bg-white flex items-center justify-center">
              <Cloud className="w-5 h-5 text-[#F9735B]" strokeWidth={1.75} />
            </span>
            <div className="flex-1">
              <p className="font-display text-[15px] font-semibold text-[#0F172A]">
                Parked Thoughts
              </p>
              <p className="text-xs text-[#475569] mt-0.5">
                Capture distractions for later.
              </p>
            </div>
            <span className="text-[11px] font-label font-semibold text-[#F9735B] bg-white border border-[#F9735B]/20 rounded-full px-2.5 py-1">
              3 parked
            </span>
            <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
          </div>

          {/* Focus Score + Streak (two cards) */}
          <div className="grid grid-cols-5 gap-3">
            <div className="col-span-3 bg-white border border-[#E2E8F0] rounded-2xl p-3.5">
              <p className="text-[10px] font-label font-semibold text-[#475569] uppercase tracking-wider">
                Focus Score
              </p>
              <div className="mt-1 flex items-end justify-between">
                <p className="font-display text-3xl font-semibold text-[#0F172A] leading-none">
                  72
                </p>
                <MiniChart />
              </div>
              <p className="text-[10px] text-[#22C55E] font-medium mt-1">
                Good momentum
              </p>
            </div>
            <div className="col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-3.5">
              <p className="text-[10px] font-label font-semibold text-[#475569] uppercase tracking-wider">
                Streak
              </p>
              <div className="mt-1 flex items-baseline gap-1.5">
                <p className="font-display text-3xl font-semibold text-[#0F172A] leading-none">
                  5
                </p>
                <span className="text-[10px] text-[#475569]">days</span>
                <Flame
                  className="w-4 h-4 text-[#F59E0B] ml-auto"
                  strokeWidth={1.75}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom nav */}
        <div className="border-t border-[#E2E8F0] bg-white px-4 py-3 grid grid-cols-4 text-[10px] font-label">
          {[
            { i: Home, l: "Home", a: true },
            { i: History, l: "History" },
            { i: BarChart3, l: "Insights" },
            { i: SettingsIcon, l: "Settings" },
          ].map((it, i) => (
            <span
              key={i}
              className={`flex flex-col items-center gap-1 ${
                it.a ? "text-[#2563EB]" : "text-[#475569]"
              }`}
            >
              <it.i className="w-4 h-4" strokeWidth={1.75} />
              <span className="font-medium">{it.l}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Clock() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="opacity-70"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function MiniChart() {
  return (
    <svg viewBox="0 0 100 30" className="w-20 h-7" aria-hidden>
      <polyline
        points="0,22 15,18 30,20 45,12 60,15 75,8 90,10 100,4"
        fill="none"
        stroke="#22C55E"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points="0,22 15,18 30,20 45,12 60,15 75,8 90,10 100,4 100,30 0,30"
        fill="#22C55E"
        opacity="0.12"
      />
    </svg>
  );
}
